import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Alert,
  Text,
  Loader,
  Group,
  Grid,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { formatValue } from "../listings/hooks";
import {
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { GeofenceMap, GeofenceValue } from "./geofence-map";
import { DataEntry } from "./data-entry";
import { FileImport, Plus, Search } from "tabler-icons-react";
import { Loader as GoogleLoader } from "@mantine/core";
import { useLoadScript } from "@react-google-maps/api";
import { Trip, AutoCheckpointGeoFence } from "@captiva/shared-types";
import { AssetConfig } from "../listings/configurations";

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ["places"];

function extractCoordsFromUrl(urlStr: string): { lat: number; lng: number } | null {
  try {
    const url = new URL(urlStr.trim());
    const host = url.hostname.toLowerCase();

    // 1. Loop through all search parameters to find any coordinate-like values
    let foundCoords: { lat: number; lng: number } | null = null;
    url.searchParams.forEach((val) => {
      if (foundCoords) return;
      const match = val.match(/(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/);
      if (match) {
        foundCoords = { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
      }
    });
    if (foundCoords) return foundCoords;

    // 2. Google Maps path check (e.g. pathname includes /@33.828,-84.375)
    if (
      host.includes("google.") &&
      (host.includes("maps") ||
        url.pathname.toLowerCase().includes("/maps") ||
        url.pathname.toLowerCase().includes("/@"))
    ) {
      const pathMatch = url.pathname.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (pathMatch) return { lat: parseFloat(pathMatch[1]), lng: parseFloat(pathMatch[2]) };
    }
  } catch (e) {
    // URL parsing failed
  }

  // 3. Fallback: Raw Coordinate string
  const fallbackMatch = urlStr.trim().match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/);
  if (fallbackMatch) {
    return { lat: parseFloat(fallbackMatch[1]), lng: parseFloat(fallbackMatch[2]) };
  }

  return null;
}

async function resolvePindropOnBackend(urlStr: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    const token = await user.getIdToken();
    if (!token) return null;

    const apiKey = process.env.REACT_APP_X_API_KEY || "";
    const baseUrl =
      process.env.REACT_APP_APP_ENV === "local" || process.env.APP_ENV === "local"
        ? "http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi"
        : "https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi";

    const response = await fetch(
      `${baseUrl}/resolve-pindrop?url=${encodeURIComponent(urlStr)}`,
      {
        method: "GET",
        headers: {
          "x-api-key": apiKey,
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("Error calling resolve-pindrop API:", err);
    return null;
  }
}

async function resolveAddressToCoordinates(address: string): Promise<{ lat: number; lng: number } | null> {
  const trimmed = address.trim();
  if (!trimmed) return null;

  // 1. Try to parse immediately if it's a long URL or raw coordinates
  const immediate = extractCoordsFromUrl(trimmed);
  if (immediate) return immediate;

  // 2. If it's a URL starting with http, try to resolve via backend (for shortened URLs)
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return await resolvePindropOnBackend(trimmed);
  }

  return null;
}

async function resolveAndGeocode(
  geocoder: any,
  addressVal: string
): Promise<{ address: string; coords: { latitude: number; longitude: number } | null }> {
  if (!addressVal || !addressVal.trim()) {
    throw new Error("Address is empty");
  }

  const coords = await resolveAddressToCoordinates(addressVal);
  if (coords) {
    try {
      const response = await geocoder.geocode({ location: coords });
      if (response.results && response.results.length > 0) {
        return {
          address: response.results[0].formatted_address,
          coords: { latitude: coords.lat, longitude: coords.lng },
        };
      }
    } catch (e) {
      // Fallback if reverse geocoding fails (e.g. farm delivery)
      return {
        address: `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`,
        coords: { latitude: coords.lat, longitude: coords.lng },
      };
    }
    return {
      address: `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`,
      coords: { latitude: coords.lat, longitude: coords.lng },
    };
  }

  // Otherwise, it's a standard text address string. Geocode normally:
  const response = await geocoder.geocode({ address: addressVal });
  if (response.results && response.results.length > 0) {
    const loc = response.results[0].geometry.location;
    return {
      address: response.results[0].formatted_address,
      coords: { latitude: loc.lat(), longitude: loc.lng() },
    };
  }
  throw new Error("Address not found");
}



export interface TripEditorProps {
  trip: Trip;
  tripConfig: AssetConfig;
  collection: string;
  onSave: (updatedTrip: any) => void;
  onDelete: (deletedTrip: any) => void;
  showLoadingIndicator?: boolean;
  dependencyList?: Record<string, any[]>;
  dependencyLabel?: Record<string, string>;
  auth?: any;
}

export function TripEditor({
  trip,
  tripConfig,
  collection: targetCollection,
  onSave,
  onDelete,
  showLoadingIndicator,
  dependencyList,
  dependencyLabel,
  auth,
}: TripEditorProps) {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "", libraries: GOOGLE_MAPS_LIBRARIES });

  const [initialValues] = useState<Record<string, any>>({ ...trip });
  const [autoCheckpoints, setAutoCheckpoints] = useState<AutoCheckpointGeoFence[]>(
    trip?.autoCheckpoints || []
  );
  const [showGeofenceModal, setShowGeofenceModal] = useState<boolean>(false);
  const [deleteCandidate, setDeleteCandidate] = useState<any | undefined>(undefined);
  const [editingGeofence, setEditingGeofence] = useState<AutoCheckpointGeoFence | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [messageColor, setMessageColor] = useState<string>("green");
  const [loading, setLoading] = useState<boolean>(showLoadingIndicator || false);
  const [addressPreview, setAddressPreview] = useState<any | null>(null);
  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string>("");

  useEffect(() => {
    setLoading(showLoadingIndicator || false);
  }, [showLoadingIndicator]);

  const form = useForm({
    initialValues,
    validate: {}, // Add validation as needed
  });

  const handleSave = async (updatedTrip: any) => {
    updatedTrip.autoCheckpoints = autoCheckpoints;
    setLoading(true);
    try {
      const geocoder = isLoaded && !loadError ? new window.google.maps.Geocoder() : null;
      
      // Auto-resolve pickup address if it is a URL or raw coordinates and coordinates are not set or mismatch
      if (geocoder && updatedTrip.pickupAddress) {
        const coords = await resolveAddressToCoordinates(updatedTrip.pickupAddress);
        if (coords) {
          updatedTrip.pickupCoordinates = { latitude: coords.lat, longitude: coords.lng };
          try {
            const response = await geocoder.geocode({ location: coords });
            if (response.results && response.results.length > 0) {
              updatedTrip.pickupAddress = response.results[0].formatted_address;
            } else {
              updatedTrip.pickupAddress = `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`;
            }
          } catch (e) {
            updatedTrip.pickupAddress = `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`;
          }
        }
      }

      // Auto-resolve delivery address if it is a URL or raw coordinates
      if (geocoder && updatedTrip.deliveryAddress) {
        const coords = await resolveAddressToCoordinates(updatedTrip.deliveryAddress);
        if (coords) {
          updatedTrip.deliveryCoordinates = { latitude: coords.lat, longitude: coords.lng };
          try {
            const response = await geocoder.geocode({ location: coords });
            if (response.results && response.results.length > 0) {
              updatedTrip.deliveryAddress = response.results[0].formatted_address;
            } else {
              updatedTrip.deliveryAddress = `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`;
            }
          } catch (e) {
            updatedTrip.deliveryAddress = `Pinpoint (${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)})`;
          }
        }
      }
    } catch (err) {
      console.error("Error auto-resolving addresses on save:", err);
    } finally {
      setLoading(false);
    }

    onSave(updatedTrip);
  }

  async function deleteAsset(deleteCandidate: any) {
    setLoading(true); // Start loading
    try {
      const docRef = doc(db, targetCollection, deleteCandidate.id);
      await deleteDoc(docRef);
      setMessageColor("green");
      setMessage("Delete successful.");
      onDelete(deleteCandidate); // Reload the list after deletion
    } catch (error: any) {
      setMessageColor("red");
      setMessage(`Delete unsuccessful. ${error.toString()}`);
    } finally {
      setLoading(false); // Stop loading
    }
    setDeleteCandidate(undefined);
  }

  async function findAddresses() {
    setAddressLoading(true);
    setAddressError("");
    setAddressPreview(null);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const pickupPromise = resolveAndGeocode(geocoder, form.values.pickupAddress);
      const deliveryPromise = resolveAndGeocode(geocoder, form.values.deliveryAddress);
      
      const [pickup, delivery] = await Promise.allSettled([
        pickupPromise,
        deliveryPromise,
      ]);
      const pickupVal = pickup.status === "fulfilled" ? pickup.value : null;
      const deliveryVal = delivery.status === "fulfilled" ? delivery.value : null;

      setAddressPreview({
        pickup: pickupVal ? pickupVal.address : null,
        pickupCoords: pickupVal ? pickupVal.coords : null,
        delivery: deliveryVal ? deliveryVal.address : null,
        deliveryCoords: deliveryVal ? deliveryVal.coords : null,
        pickupError: pickup.status === "rejected" ? (pickup.reason instanceof Error ? pickup.reason.message : String(pickup.reason)) : null,
        deliveryError: delivery.status === "rejected" ? (delivery.reason instanceof Error ? delivery.reason.message : String(delivery.reason)) : null,
      });

      if (pickupVal && pickupVal.coords) {
        form.setFieldValue("pickupCoordinates", pickupVal.coords);
      }
      if (deliveryVal && deliveryVal.coords) {
        form.setFieldValue("deliveryCoordinates", deliveryVal.coords);
      }
    } catch (err) {
      setAddressError("Error finding addresses. Please try again.");
    } finally {
      setAddressLoading(false);
    }
  }

  function acceptPreviewedAddresses() {
    if (addressPreview) {
      if (addressPreview.pickup) {
        form.setFieldValue("pickupAddress", addressPreview.pickup);
        if (addressPreview.pickupCoords) {
          form.setFieldValue("pickupCoordinates", addressPreview.pickupCoords);
        }
      }
      if (addressPreview.delivery) {
        form.setFieldValue("deliveryAddress", addressPreview.delivery);
        if (addressPreview.deliveryCoords) {
          form.setFieldValue("deliveryCoordinates", addressPreview.deliveryCoords);
        }
      }
      setAddressPreview(null);
    }
  }

  function showAddressPreview() {
    if(!isLoaded || loadError) {
      return false;
    }
    if (!form.values.pickupAddress || !form.values.deliveryAddress)
      return false;
    return true;
  }

  return (
    <>
      {showAddressPreview() && (
        <Box
          my={12}
          style={{
            background: "#f8f8fa",
            border: "solid 1px #eee",
            padding: 12,
            borderRadius: 6,
          }}
        >
          <Button
            size="sm"
            color="cap-navy"
            variant="outline"
            leftIcon={<Search size={16} />}
            mt={8}
            onClick={findAddresses}
            disabled={addressLoading}
          >
            Lookup Addresses
          </Button>
          {addressLoading && <GoogleLoader size="sm" mt={8} />}
          {addressError && (
            <Alert color="red" mt={8}>
              {addressError}
            </Alert>
          )}
          {addressPreview && (
            <div
              style={{
                marginTop: 8,
                marginBottom: 8,
                background: "#f8f8fa",
                padding: 12,
                borderRadius: 6,
              }}
            >
              <Text size="sm" weight={500}>
                Preview found addresses:
              </Text>
              <div>
                <b>Pickup:</b>{" "}
                {addressPreview.pickup || (
                  <span style={{ color: "red" }}>
                    {addressPreview.pickupError}
                  </span>
                )}
              </div>
              <div>
                <b>Delivery:</b>{" "}
                {addressPreview.delivery || (
                  <span style={{ color: "red" }}>
                    {addressPreview.deliveryError}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                color="cap-navy"
                variant="outline"
                leftIcon={<FileImport size={16} />}
                mt={8}
                onClick={acceptPreviewedAddresses}
              >
                Accept
              </Button>
            </div>
          )}
        </Box>
      )}
      <form onSubmit={form.onSubmit((values) => handleSave(values))}>
        {/* Render trip fields here, similar to AssetEditor, but bespoke for trips */}
        {Object.keys(initialValues)
          .filter((k) => tripConfig[k])
          .sort((a, b) => (tripConfig[a].rank ?? 0) - (tripConfig[b].rank ?? 0))
          .map((key) => (
            <div
              key={key}
              style={{
                marginTop: "1em",
              }}
            >
              <DataEntry
                label={tripConfig[key]?.label}
                edit={tripConfig[key]?.editable}
                inputProps={form.getInputProps(key)}
                dataType={tripConfig[key]?.type || "STRING"}
                form={form}
                fieldKey={key}
                selectOptions={dependencyList && dependencyList[key]}
                selectLabelKey={dependencyList && dependencyLabel ? dependencyLabel[key] : undefined}
                value={formatValue(tripConfig[key]?.type || "STRING", form.values[key])}
                shouldIncludeTimeForDate={false}
                decimalPrecision={tripConfig[key]?.decimalPrecision}
              />
            </div>
          ))}
        {/* ...fields... */}
        <div style={{ marginTop: 24, marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text size="md" weight={500}>
              Auto-Checkpoints
            </Text>
            <Button
              size="sm"
              color="cap-navy"
              variant="outline"
              onClick={() => {
                setEditingGeofence(null);
                setEditingIndex(null);
                setShowGeofenceModal(true);
              }}
              leftIcon={<Plus size={16} />}
            >
              Checkpoint
            </Button>
          </div>
          {autoCheckpoints.length === 0 && (
            <Text size="sm" color="dimmed">
              No auto-checkpoints configured.
            </Text>
          )}
          {autoCheckpoints.map((g, idx) => (
            <div
              key={idx}
              style={{
                border: "1px solid #eee",
                borderRadius: 6,
                padding: 10,
                marginTop: 8,
                marginBottom: 8,
                background: "#fafbfc",
              }}
            >
              <b>{g?.extras?.landmark}</b> ({g?.latitude?.toFixed(5)},{" "}
              {g?.longitude?.toFixed(5)}) - {g?.radius}m
              <div style={{ fontSize: 12, color: "#888" }}>
                {g?.extras?.note}
              </div>
              <Group spacing={6} mt={4}>
                <Button
                  size="sm"
                  variant="subtle"
                  color="cap-navy"
                  onClick={() => {
                    setEditingGeofence(g);
                    setEditingIndex(idx);
                    setShowGeofenceModal(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    setAutoCheckpoints(
                      autoCheckpoints.filter((_, i) => i !== idx)
                    )
                  }
                >
                  Delete
                </Button>
              </Group>
            </div>
          ))}
        </div>
        {showGeofenceModal && (
          <Modal
            opened={showGeofenceModal}
            onClose={() => setShowGeofenceModal(false)}
            title={editingGeofence ? "Edit Checkpoint" : "Add Checkpoint"}
            size="lg"
          >
            <GeofenceMap
              value={editingGeofence as GeofenceValue | null}
              onCancel={() => setShowGeofenceModal(false)}
              onChange={(g) => {
                if (editingIndex !== null) {
                  const updated = [...autoCheckpoints];
                  updated[editingIndex] = g;
                  setAutoCheckpoints(updated);
                } else {
                  setAutoCheckpoints([...autoCheckpoints, g]);
                  form.setFieldValue("autoCheckpoints", [
                    ...autoCheckpoints, g
                  ]);
                }
                setShowGeofenceModal(false);
              }}
            />
          </Modal>
        )}
        <Button
          size="md"
          color="cap-navy"
          fullWidth
          type="submit"
          mt="md"
          disabled={loading}
        >
          {loading ? <Loader size="sm" color="white" /> : "Save"}
        </Button>
        {message && <Alert color={messageColor}>{message}</Alert>}
      </form>
      {initialValues.id && (
        <>
          {!!deleteCandidate ? (
            <Grid my="md">
              <Grid.Col
                span={4}
                styles={{ display: "flex", alignItems: "center" }}
              >
                <Text size="lg">Are you sure?</Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Button
                  variant="default"
                  color="gray"
                  fullWidth
                  onClick={() => setDeleteCandidate(undefined)}
                  disabled={loading} // Disable button while loading
                >
                  Cancel
                </Button>
              </Grid.Col>
              <Grid.Col span={4}>
                <Button
                  color="red"
                  onClick={() => deleteAsset(trip)}
                  fullWidth
                  disabled={loading} // Disable button while loading
                >
                  {loading ? (
                    <Loader size="sm" color="white" />
                  ) : (
                    "Yes - Delete"
                  )}
                </Button>
              </Grid.Col>
            </Grid>
          ) : (
            <Button
              size="md"
              color="red"
              variant="outline"
              fullWidth
              mt="md"
              onClick={() => setDeleteCandidate(trip)}
              disabled={loading} // Disable button while loading
            >
              Delete
            </Button>
          )}
        </>
      )}
    </>
    // <Modal opened={opened} onClose={onClose} title="Trip" size="lg">
  );
}
